import { apiClient, apiClientSilent } from "api/trpc/trpc-client";
import AuthStore from "auth/AuthStore";
import { authCache } from "cache/authCache";
import { orgContextCache } from "cache/orgContextCache";
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils";
import { capitalize } from "reusable/utils/string-utils";
import { deferAction } from "reusable/utils/utils";
import { canSeeWorkItems, hasFeatureToggleBypass, notNull } from '@gap/utils/utils'
import variables from "variables/variables";
import RegionRouterStore from "features/main/RegionRouterStore";
import { snackbar } from "reusable/mui/snackbar/snackbar";
import { FeatureActionEnum, FeatureEnum } from "@gap/backend/app-services/feature/models/FeatureEnum";
import { Sentry } from "sentry/sentry";
import * as SentryBrowser from '@sentry/browser'
import { Analytics, AnalyticsUserType } from "analytics/analytics";
import Loader from "reusable/mobx-helpers/Loader";
import authMessageWithHome from "reusable/mui/modals/auth-message-with-home";
import createGapOrgPopup from "reusable/mui/modals/create-gap-org-popup";
import { countryNameFromCode } from "@gap/utils/country/countryList";
import { TRPCClientError } from "@trpc/client";
import { ThemeStore } from "theme/ThemeStore";
import { UserOrgWithOrgDto } from "@gap/backend/app-services/user-org/models/UserOrgWithOrgDto";
import { featureToggleEnum, FeatureToggleEnum } from "@gap/backend/app-services/feature-toggle/models/FeatureToggleEnum";
import { UserDto } from "@gap/backend/app-services/user/models/UserDto";
import { UserOrgDto } from "@gap/backend/app-services/user-org/models/UserOrgDto";
import { GapError } from "@trpc-endpoints/trpc/error-handler/GapError";
import { forceLogoutWithReason } from "auth/logout-reason";
import { GettingStartedStep } from "@gap/backend/app-services/user/models/UserEntityDtoSchema";
import { completedVisibleStepCount, getVisibleGettingStartedSteps } from "features/gap-components/getting-started/gettingStartedSteps";
import MeetingAnalytics from 'analytics/MeetingAnalytics'
import { canonicalStage } from '@gap/utils/stage'
import { TierNamesDto } from '@gap/utils/tier/tier-config'

/**
 * NO-455: private clients ("client visibility") are a multi-user-plan feature. Blocked on
 * the individual plans (Core, Grow) and legacy Start; Trial previews it; the other legacy
 * paid tiers are grandfathered in. PR #471 (Chargebee) layers the entitlement check on top
 * of this legacy fallback rule.
 */
const tiersWithoutClientVisibility: TierNamesDto[] = ['Start', 'Core', 'Grow']

interface OrgGuardStoreProps {
   orgId?: string
   regionRouterStore: RegionRouterStore
   authStore: AuthStore
   themeStore: ThemeStore
}

export default class OrgGuardStore {
   sp
   loader = new Loader()
   networkError = false
   // This is private. Every other file that needs it will only be rendered if this exists. Please use orgGuardStore.userData
   private userInfo: UserDto.info | undefined

   constructor(sp: OrgGuardStoreProps) {
      this.sp = sp
      if (!this.sp.authStore.isGuest) {
         deferAction(() => this.sp.authStore.updateUserFromAmplify())
      }
   }

   get loading() {
      return !this.userInfo || this.loader.isLoading
   }

   get orgId() {
      return this.sp.orgId
   }

   get userData() {
      return notNull(this.userInfo, 'User info was not set')
   }

   get canSeeWorkItems() {
      return canSeeWorkItems(this.userOrg)
   }

   get userOrg() {
      return notNull(this.userData.userOrg)
   }

   get userIsDeactivated() {
      return this.userInfo?.userOrg?.isDeactivated
   }

   get trialEndDate() {
      return this.userInfo?.userOrg?.trialEndDate
   }

   get isOnTrial() {
      return this.userInfo?.userOrg?.tierName === 'Trial'
   }

   /** Whether this org can make clients private — see tiersWithoutClientVisibility above. */
   get canUseClientVisibility(): boolean {
      const tierName = this.userInfo?.userOrg?.tierName
      return !!tierName && !tiersWithoutClientVisibility.includes(tierName)
   }

   get isGap() {
      return this.orgId === variables.gapOrgId
   }

   get isDeveloper() {
      return variables.devTeam.some(name => this.userInfo?.email.startsWith(name) && this.userInfo?.email.endsWith(`@${variables.gapDomain}`))
   }

   get isDevOrProductTeam() {
      return [...variables.devTeam, ...variables.productTeam].some(name => this.userInfo?.email.startsWith(name) && this.userInfo?.email.endsWith(`@${variables.gapDomain}`))
   }

   get isGapInTestEnvOrAbove() {
      return this.isGap && ['test', 'preprod', 'prod'].includes(canonicalStage(notNull(variables.stage)))
   }

   get org(): UserOrgWithOrgDto | undefined {
      return this.userInfo?.orgs?.find(org => org.orgId === this.orgId)
   }

   get isTeamMember() {
      return this.org?.roleName === 'Team Member'
   }

   get isAdmin() {
      return this.org?.roleName?.toLowerCase() === 'admin'
   }

   get hasGapSampleFeatureToggle() {
      return this.userInfo?.userOrg.featureToggles.includes('gap-sample-feature-toggle')
   }

   get userHasBusinessPlanPermission() {
      if (this.userData.userOrg?.orgType === 'client') {
         return this.userData.userOrg?.permissions.includes('business-plan') || this.isAdmin
      }
      return true
   }

   get userHasMyOrganisationPermission() {
      return this.doesUserHaveFeaturePermission({ featureId: 'my-organisation' })
   }

   get featureToggles(): Record<FeatureToggleEnum, boolean> {
      return (Object.values(featureToggleEnum) as FeatureToggleEnum[]).reduce(
         (acc, featureToggle) => {
            acc[featureToggle] = this.userInfo?.userOrg.featureToggles.includes(featureToggle) || false
            return acc
         },
         {} as Record<FeatureToggleEnum, boolean>
      )
   }

   get isGapHqEmail() {
      return hasFeatureToggleBypass(this.userInfo?.email)
   }

   /** Whether the org may sync clients from FYI. */
   get hasFyiClientSyncAccess(): boolean {
      return this.hasFeatureToggleAccess('fyi-client-sync')
   }

   get showGapAcademy() {
      return this.userData.userOrg.orgType === 'advisor' &&
         !this.userData.userOrg.isDeactivated && 
         this.userData.circleId
   }

   /**
    * Set the current org in the user org list to what every you give it
    */
   set org(updatedOrg: UserOrgWithOrgDto) {
      this.userData.orgs = (this.userData.orgs || []).map(org =>
         org.orgId === updatedOrg.orgId ? updatedOrg : org
      )
   }
   
   /** Whether the user has access to a feature toggle (toggle is on OR user is @thegaphq.com) */
   hasFeatureToggleAccess = (toggle: FeatureToggleEnum): boolean => {
      return this.featureToggles[toggle] || this.isGapHqEmail
   }

   /**
    * Main fetch function. Use the orgId in the url to fetch my user. Handle as per comments
    */
   fetch = async () => {
      await this.loader.executeLoading(async () => {
         try {
            // Set the requested orgId in the cache.
            orgContextCache.authOrgId.set(this.orgId)

            // Get userInfo. The api automaticly adds the orgId orgContextCache.authOrgId in the headers, requesting the userOrg to be that org
            let userInfo: UserDto.info
            const cachedUserInfo = window.localStorage.getItem('cached.userInfo')
            if (cachedUserInfo) {
               userInfo = JSON.parse(cachedUserInfo)
            } else {
               try {
                  userInfo = await apiClientSilent.user.getMyUserInfo.query()
               } catch (error: any) {
                  if (error.message === GapError.messages.auth.loginExpired) {
                     await forceLogoutWithReason({ reason: 'sessionExpired' })
                  }
                  throw error
               }
            }

            this.setUserInfo(userInfo)

            // The user doesnt have any orgs
            if (!userInfo.orgs?.length) {
               await this.noOrgHandler()
            } else if (this.orgId && userInfo.userOrg.orgId !== this.orgId) {
               // The requested org is not an org we have access to
               await authMessageWithHome({
                  content: `Your user does not have access to the requested organisation. Please ensure your url is correct or go to your organisation using the home button below`,
                  title: "Access Denied",
                  callback: () => this.sp.regionRouterStore.navigate('/')
               })
            } else {
               // We have access to the org. Set all needed values
               this.setOrg(this.orgId || userInfo.userOrg.orgId)
            }
         } catch (error) {
            if (error instanceof TRPCClientError) {
               if (error.message.startsWith('Failed to fetch')) {
                  this.networkError = true
               }
               if (error.shape?.message === 'User does not exist' && this.sp.authStore.isXeroUser) {
                  this.sp.regionRouterStore.navigate('/trial')
               }
            }
            throw error
         }
      })
   }

   /**
    * This function checks if we have permission based on our role set in
    * @param permission permission
    * @returns boolean indicating if we have permission
    */
   doesUserHaveFeaturePermission = (permission: { featureId: FeatureEnum, actionId?: FeatureActionEnum }) => {
      return !this.loading && !!this.userData.userOrg.features.some(feature =>
         feature.featureId === permission.featureId &&
         (!permission.actionId || feature.actions.some(action => action.actionId === permission.actionId))
      )
   }

   /**
    * Set user and org properties in Sentry and MixPanel
    */
   setOrgPropertiesForSentryAndMixpanel = () => {
      if (this.sp.orgId) {
         Sentry.setOrgProperties({
            id: this.orgId,
            name: this.org?.name,
         })
         Analytics.setUserProperties({
            "User type": (this.org?.orgType && capitalize(this.org?.orgType)) as AnalyticsUserType,
            "User role": this.org?.roleName,
         })
         if (this.org) {
            Analytics.setOrgProperties({
               orgId: this.org.orgId,
               $name: this.org.name,
               Type: capitalize(this.org.orgType),
               Country: this.org.countryCode ? countryNameFromCode(this.org.countryCode).name : undefined,
               Website: this.org.website,
               "Company size": this.org.orgSize,
            })
         }
      }
   }

   /**
    * This function returns the prefix for org routes, ex. org/00gap when 00gap is given
    * @param forcedOrgId id of the org
    * @returns /org/${forcedOrgId}
    */
   getOrgPrefix = (forcedOrgId?: string) => {
      return `org/${forcedOrgId || this.orgId || this.userInfo?.userOrg.orgId || this.userInfo?.orgs?.[0]?.orgId || 'org_id_not_set'}`
   }

   /**
    * Sets the userInfo in this store and updates our Sentry & Mixpanel user values
    */
   setUserInfo = (userInfo: UserDto.info) => {
      this.userInfo = userInfo
      this.enforceThemeForNonDevs()

      if (userInfo) {
         Sentry.setUserProperties({
            id: userInfo.id,
            email: userInfo.email,
         })

         Analytics.setUserId(userInfo.id)
         Analytics.setUserProperties({
            $email: userInfo.email,
            $first_name: userInfo.firstName,
            $last_name: userInfo.lastName,
            "Job title": userInfo.jobTitle,
            ...(userInfo.trialData ? {
               "Situation": userInfo.trialData.situation,
               "Barriers": userInfo.trialData.barriers,
            } : {})
         })

         const org = userInfo.orgs?.find(org => org.orgId === userInfo.userOrg.orgId)

         Analytics.setOrgProperties({
            orgId: userInfo.userOrg.orgId,
            "Company size": org?.orgSize,
            "Plan": userInfo.userOrg.tierName,
            "Trial expiry": userInfo.userOrg.trialEndDate,
            "Account status": Analytics.getOrgAccountStatusFromDto(userInfo.userOrg),
         })
      }
   }

   /**
    * This function will set the correct userinfo and org. When using the org switcher it will also navigate you to the new orgs homepage
    * @param orgId Requested org to set
    * @param navigateToOrgDefault Indicate if we want to navigate to the orgs homepage on completion
    */
   setOrg = async (orgId: string, navigateToOrgDefault?: boolean) => {
      await this.loader.executeLoading(async () => {
         // This is the org we want to switch to
         const org = this.userData.orgs?.find(org => org.orgId === orgId)
         if (org) {
            // This will be set when swiching orgs using the org switcher, so force a navigation to their home page     
            if (navigateToOrgDefault) {
               return this.sp.regionRouterStore.navigate(this.getOrgPrefix(orgId), true)
            }
            orgContextCache.authOrgId.set(org.orgId)

            if (this.userData.userOrg.orgId !== orgId) {
               // Only fetch is the requested org is not set in our userInfo.userOrg (ex. when switching orgs)
               const userInfo = await apiClient.user.getMyUserInfo.query()
               this.setUserInfo(userInfo)
            }
         }
      })
   }

   /**
    * If no orgs are found, try and detect if it is a new xero user with no orgs. Otherwise open the no orgs modal (do we still need this though?)
    */
   noOrgHandler = async () => {
      const email = this.sp.authStore.userAttributes?.email ?? ''
      const gapUserShouldBeCreated = await apiClient.org.gapUserShouldBeCreated.query({ email })

      if (!gapUserShouldBeCreated) {
         deferAction(async () => {
            const signedInWithXero = authCache.signedInWithXero.get()
            // If the user has signed in using Xero but didn't have a Gap account
            if (signedInWithXero) {
               snackbar({ title: 'Oops, no account found! Sign up for a trial by following these steps.' })
               this.sp.regionRouterStore.navigate('/trial')
            }
         })
      } else {
         createGapOrgPopup({
            firstName: this.sp.authStore.userAttributes?.given_name,
            lastName: this.sp.authStore.userAttributes?.family_name,
            email
         })
      }
   }

   /**
    * Update selected attributes within the userOrg property.
    * @param updates Updates within the userOrg
    */
   updateUserOrgData = (updates: Partial<UserOrgDto.Info>) => {
      if (this.userData.userOrg) {
         this.userData.userOrg = {
            ...this.userData.userOrg,
            ...updates,
         }
      }
   }

   completeGettingStartedStep = (step: GettingStartedStep) => {
      const current = this.userData.onboarding?.gettingStartedSteps ?? []
      if (current.includes(step)) return
      const newSteps = [...current, step] as typeof current
      this.userData.onboarding = {
         ...this.userData.onboarding,
         gettingStartedSteps: newSteps,
      }

      apiClient.user.updateMyOnboardingProgress.mutate({ gettingStartedSteps: newSteps }).catch(error => {
         SentryBrowser.captureException(error, { tags: { component: 'OrgGuardStore', action: 'completeGettingStartedStep', step, newSteps: newSteps.join(', ') } })
      })
      const visibleSteps = getVisibleGettingStartedSteps({
         hasMyOrganisationPermission: this.userHasMyOrganisationPermission,
         hasCalendarToggle: this.hasFeatureToggleAccess('calendar-integration'),
      })
      if (completedVisibleStepCount({ completedSteps: newSteps, visibleSteps }) === visibleSteps.length) {
         MeetingAnalytics.onOnboardingChecklistCompleted()
      }
   }

   /** Force non-developers to light mode, clearing any cached dark/os-defined preference */
   private enforceThemeForNonDevs = () => {
      if (!this.isDeveloper && this.sp.themeStore.themeVariables.themeMode !== 'light') {
         this.sp.themeStore.resetToLightMode()
      }
   }
}

export const { Provider: OrgGuardStoreProvider, useProvidedStore: useOrgGuardStore, useOptionalStore: useOptionalOrgGuardStore } = createStoreContext(OrgGuardStore)
